import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth.jsx'
import { Plus, LogOut, RotateCcw, Eye, Pencil, Trash2, Search, ChevronUp, ChevronDown, Copy } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/authService'
import ClienteDetalleModal from '../components/ClienteDetalleModal'
import { useLocation } from 'react-router-dom'

const PAGE_SIZE = 10

function Dashboard() {
  const { user, logout, hasPermission } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [clientes, setClientes] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroEstado, setFiltroEstado] = useState(() => localStorage.getItem('clientesFiltroEstado') || '')
  const [filtroFecha, setFiltroFecha] = useState(() => localStorage.getItem('clientesFiltroFecha') || '')
  const [filtroGlobal, setFiltroGlobal] = useState(() => localStorage.getItem('clientesFiltroGlobal') || '')
  const [mesAnioDeuda, setMesAnioDeuda] = useState('')
  const [page, setPage] = useState(() => Number(localStorage.getItem('clientesPagina')) || 1)
  const [detalleCliente, setDetalleCliente] = useState(null)
  const [sortBy, setSortBy] = useState('id')
  const [sortOrder, setSortOrder] = useState('desc')

  useEffect(() => {
    cargarClientes()
  }, [])

  useEffect(() => { localStorage.setItem('clientesFiltroEstado', filtroEstado) }, [filtroEstado])
  useEffect(() => { localStorage.setItem('clientesFiltroFecha', filtroFecha) }, [filtroFecha])
  useEffect(() => { localStorage.setItem('clientesFiltroGlobal', filtroGlobal) }, [filtroGlobal])
  useEffect(() => { localStorage.setItem('clientesPagina', page) }, [page])

  const cargarClientes = async () => {
    try {
      setLoading(true)
      const response = await api.get('/clientes')
      setClientes(response.data.data || [])
    } catch (error) {
      console.error('Error cargando clientes:', error)
      toast.error('Error al cargar los clientes')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
      toast.success('Sesión cerrada correctamente')
    } catch (error) {
      toast.error('Error al cerrar sesión')
    }
  }

  const actualizarEstadoMasivo = async () => {
    if (!mesAnioDeuda.trim()) {
      toast.error('Por favor ingrese el mes y año')
      return
    }

    if (!confirm(`¿Está seguro de cambiar a DEUDA todos los registros con fecha ${mesAnioDeuda}?`)) {
      return
    }

    try {
      const response = await api.post('/clientes/actualizar-estado', {
        mesAnioDeuda: mesAnioDeuda.trim()
      })
      
      toast.success(response.data.message)
      setMesAnioDeuda('')
      cargarClientes()
    } catch (error) {
      toast.error('Error al actualizar estados')
    }
  }

  const getEstadoClass = (estado) => {
    switch (estado) {
      case 'OK': return 'badge-success'
      case 'DEUDA': return 'badge-danger'
      case 'CANCELADO': return 'badge-secondary'
      case 'MOROSO': return 'badge-dark'
      case 'OTROS': return 'badge-warning'
      default: return 'badge-secondary'
    }
  }

  const getRowClass = (estado) => {
    switch (estado) {
      case 'OK': return 'row-ok'
      case 'DEUDA': return 'row-deuda'
      case 'CANCELADO': return 'row-cancelado'
      case 'MOROSO': return 'row-moroso'
      case 'OTROS': return 'row-otros'
      default: return ''
    }
  }

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(col)
      setSortOrder('asc')
    }
    setPage(1)
  }

  // Filtros y ordenamiento
  // Función para convertir MESYYYY a YYYYMM
  function convertirFechaMesAnio(fecha) {
    if (!fecha) return '000000'
    const meses = {
      'ENERO': '01', 'FEBRERO': '02', 'MARZO': '03', 'ABRIL': '04',
      'MAYO': '05', 'JUNIO': '06', 'JULIO': '07', 'AGOSTO': '08',
      'SEPTIEMBRE': '09', 'OCTUBRE': '10', 'NOVIEMBRE': '11', 'DICIEMBRE': '12'
    }
    const mes = fecha.substring(0, fecha.length - 4).toUpperCase()
    const anio = fecha.substring(fecha.length - 4)
    return anio + (meses[mes] || '00')
  }

  const clientesFiltrados = clientes.filter(cliente => {
    // Filtro global
    const global = filtroGlobal.trim().toLowerCase()
    const cumpleGlobal = !global || [
      cliente.id,
      cliente.empresa,
      cliente.nombre,
      cliente.numero,
      cliente.estado,
      cliente.placa,
      cliente.mensaje,
      cliente.desde_mesanio_deuda,
      cliente.fecha,
      cliente.tipo_cliente,
      cliente.fecha_hora_envio,
      cliente.repetir_cada_dias
    ].some(val => (val ? String(val).toLowerCase().includes(global) : false))

    const cumpleEstado = !filtroEstado || cliente.estado === filtroEstado
    // Filtro por fecha: mostrar <= fecha
    let cumpleFecha = true
    if (filtroFecha) {
      const fechaFiltro = convertirFechaMesAnio(filtroFecha)
      const fechaCliente = convertirFechaMesAnio(cliente.desde_mesanio_deuda)
      cumpleFecha = fechaCliente <= fechaFiltro
    }
    return cumpleGlobal && cumpleEstado && cumpleFecha
  }).sort((a, b) => {
    let valA = a[sortBy]
    let valB = b[sortBy]
    if (valA === undefined || valA === null) valA = ''
    if (valB === undefined || valB === null) valB = ''
    if (typeof valA === 'string') valA = valA.toLowerCase()
    if (typeof valB === 'string') valB = valB.toLowerCase()
    if (valA < valB) return sortOrder === 'asc' ? -1 : 1
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1
    return 0
  })

  // Paginación
  const totalPages = Math.ceil(clientesFiltrados.length / PAGE_SIZE)
  const clientesPagina = clientesFiltrados.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Contenido Principal */}
      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 py-4">
        {/* Filtros y Acciones */}
        <div className="card mb-6">
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Filtro por Estado */}
              <div>
                <label className="form-label">Filtrar por Estado</label>
                <select
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                  className="form-select"
                >
                  <option value="">Todos los estados</option>
                  <option value="OK">OK</option>
                  <option value="DEUDA">DEUDA</option>
                  <option value="CANCELADO">CANCELADO</option>
                  <option value="MOROSO">MOROSO</option>
                  <option value="OTROS">OTROS</option>
                </select>
              </div>

              {/* Filtro por Fecha */}
              <div>
                <label className="form-label">Filtrar por Fecha</label>
                <input
                  type="text"
                  value={filtroFecha}
                  onChange={(e) => setFiltroFecha(e.target.value)}
                  placeholder="Ejemplo: ENERO2024"
                  className="form-input"
                />
              </div>

              {/* Filtro global */}
              <div className="md:col-span-2 lg:col-span-1 flex flex-col justify-end">
                <label className="form-label">Búsqueda global</label>
                <div className="relative">
                  <input
                    type="text"
                    value={filtroGlobal}
                    onChange={e => { setFiltroGlobal(e.target.value); setPage(1); }}
                    placeholder="Buscar por cualquier campo..."
                    className="form-input pr-10"
                  />
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {/* Actualización Masiva */}
              {hasPermission('CAMBIAR_DEUDA') && (
                <div>
                  <label className="form-label">Cambiar a DEUDA</label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={mesAnioDeuda}
                      onChange={(e) => setMesAnioDeuda(e.target.value)}
                      placeholder="Ejemplo: ENERO2024"
                      className="form-input flex-1"
                    />
                    <button
                      onClick={actualizarEstadoMasivo}
                      className="btn btn-warning"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Botón Agregar Cliente */}
              {hasPermission('AGREGAR_CLIENTE') && (
                <div className="flex items-end">
                  <button
                    onClick={() => navigate('/clientes/nuevo')}
                    className="btn btn-success w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Cliente
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabla de Clientes */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">
              Clientes ({clientesFiltrados.length})
            </h2>
          </div>
          <div className="card-body p-0">
            <div className="overflow-x-auto w-full">
              <table className="table min-w-full text-xs">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('id')} className="cursor-pointer select-none text-xs px-2 py-2 hover:bg-gray-100">ID {sortBy === 'id' && (sortOrder === 'asc' ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />)}</th>
                    <th onClick={() => handleSort('tipo_cliente')} className="cursor-pointer select-none text-xs px-2 py-2 hover:bg-gray-100">Tipo {sortBy === 'tipo_cliente' && (sortOrder === 'asc' ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />)}</th>
                    <th onClick={() => handleSort('empresa')} className="cursor-pointer select-none text-xs px-2 py-2 hover:bg-gray-100">Empresa {sortBy === 'empresa' && (sortOrder === 'asc' ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />)}</th>
                    <th onClick={() => handleSort('nombre')} className="cursor-pointer select-none text-xs px-2 py-2 hover:bg-gray-100">Nombre {sortBy === 'nombre' && (sortOrder === 'asc' ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />)}</th>
                    <th onClick={() => handleSort('placa')} className="cursor-pointer select-none text-xs px-2 py-2 hover:bg-gray-100">Placa {sortBy === 'placa' && (sortOrder === 'asc' ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />)}</th>
                    <th onClick={() => handleSort('estado')} className="cursor-pointer select-none text-xs px-2 py-2 hover:bg-gray-100">Estado {sortBy === 'estado' && (sortOrder === 'asc' ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />)}</th>
                    <th onClick={() => handleSort('desde_mesanio_deuda')} className="cursor-pointer select-none text-xs px-2 py-2 hover:bg-gray-100">Desde {sortBy === 'desde_mesanio_deuda' && (sortOrder === 'asc' ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />)}</th>
                    <th className="text-center text-xs px-2 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {clientesPagina.map((cliente) => (
                    <tr key={cliente.id} className={getRowClass(cliente.estado)}>
                      <td className="text-xs px-2 py-2 font-medium">{cliente.id}</td>
                      <td className="text-xs px-2 py-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          cliente.tipo_cliente === 'PROGRAMADO' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {cliente.tipo_cliente || 'NORMAL'}
                        </span>
                      </td>
                      <td className="text-xs px-2 py-2" title={cliente.empresa}>
                        {cliente.empresa && cliente.empresa.length > 15 ? cliente.empresa.slice(0, 15) + '...' : cliente.empresa}
                      </td>
                      <td className="text-xs px-2 py-2" title={cliente.nombre}>
                        {cliente.nombre && cliente.nombre.length > 15 ? cliente.nombre.slice(0, 15) + '...' : cliente.nombre}
                      </td>
                      <td className="text-xs px-2 py-2 font-mono" title={cliente.placa}>
                        {cliente.placa && cliente.placa.length > 10 ? cliente.placa.slice(0, 10) + '...' : cliente.placa}
                      </td>
                      <td className="text-xs px-2 py-2">
                        <span className={`badge ${getEstadoClass(cliente.estado)} text-xs`}>
                          {cliente.estado}
                        </span>
                      </td>
                      <td className="text-xs px-2 py-2" title={cliente.desde_mesanio_deuda}>
                        {cliente.desde_mesanio_deuda && cliente.desde_mesanio_deuda.length > 12 ? cliente.desde_mesanio_deuda.slice(0, 12) + '...' : (cliente.desde_mesanio_deuda || '-')}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            className="btn btn-secondary btn-sm p-1"
                            title="Ver Detalle"
                            onClick={() => setDetalleCliente(cliente)}
                          >
                            <Eye className="w-3 h-3" />
                          </button>
                          {hasPermission('EDITAR_CLIENTE') && (
                            <button
                              onClick={() => navigate(`/clientes/editar/${cliente.id}`)}
                              className="btn btn-warning btn-sm p-1"
                              title="Editar"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                          <button
                            className="btn btn-info btn-sm p-1"
                            title="Copiar"
                            onClick={() => {
                              const { id, fecha, ...rest } = cliente
                              navigate('/clientes/nuevo', { state: { clienteCopiado: rest } })
                            }}
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                          {hasPermission('ELIMINAR_CLIENTE') && (
                            <button
                              onClick={async () => {
                                if (confirm('¿Está seguro de eliminar este cliente?')) {
                                  try {
                                    await api.delete(`/clientes/${cliente.id}`)
                                    toast.success('Cliente eliminado correctamente')
                                    cargarClientes()
                                  } catch (error) {
                                    toast.error('Error al eliminar el cliente')
                                  }
                                }
                              }}
                              className="btn btn-danger btn-sm p-1"
                              title="Eliminar"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Paginación */}
            <div className="flex justify-between items-center px-6 py-4 border-t border-gray-100">
              <div className="text-sm text-gray-500">
                Página {page} de {totalPages} ({clientesFiltrados.length} clientes)
              </div>
              <div className="flex gap-1">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePageChange(1)}
                  disabled={page === 1}
                >
                  {'<<'}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  {'<'}
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                >
                  {'>'}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={page === totalPages}
                >
                  {'>>'}
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Modal de detalle */}
        <ClienteDetalleModal
          cliente={detalleCliente}
          onClose={() => setDetalleCliente(null)}
        />
      </div>
    </div>
  )
}

export default Dashboard 